<script lang="ts">
	import { create } from "@bufbuild/protobuf";
	import { timestampDate, timestampFromDate } from "@bufbuild/protobuf/wkt";
	import { Heading, Helper } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import { WindowSchema, type Poll } from "#gen/forms/v1/poll_pb.js";
	import type { User } from "#gen/identities/v1/identities_pb.js";
	import Toggle from "#lib/components/Toggle.svelte";
	import DateTimeInput from "#lib/components/calendar/DateTimeInput.svelte";
	import { DEFAULT_DATE_FORMAT_OPTIONS, DEFAULT_DATE_PLACEHOLDER } from "#lib/components/page/components/poll/constants.js";
	import { isEndBeforeOrAtStart } from "#lib/components/page/components/poll/utils.js";
	import type { Writable } from "svelte/store";

	interface Props {
		poll: Poll;
		onChange?: (poll: Poll) => void;
	}

	const user: Writable<User> = getContext("user");

	let { poll, onChange }: Props = $props();

	let showEndBeforeStartError = $state(false);

	const startDate = $derived(poll.window?.start ? timestampDate(poll.window.start) : undefined);
	const endDate = $derived(poll.window?.end ? timestampDate(poll.window.end) : undefined);
	const inputColor = $derived(showEndBeforeStartError ? "red" : "default");

	function handleChange(date: Date, type: "start" | "end") {
		const newWindow = create(WindowSchema, { ...poll.window });
		if (type === "start") {
			newWindow.start = timestampFromDate(date);
		} else {
			newWindow.end = timestampFromDate(date);
		}
		if (isEndBeforeOrAtStart(newWindow.start, newWindow.end)) {
			showEndBeforeStartError = true;
			return;
		}
		showEndBeforeStartError = false;
		onChange?.({ ...poll, window: newWindow });
	}

	function handleEndToggle(event: Event) {
		const target = event.target as HTMLInputElement;
		const newWindow = create(WindowSchema, { ...poll.window });
		if (target.checked) {
			newWindow.end = undefined;
		} else {
			newWindow.end = timestampFromDate(new Date());
		}
		showEndBeforeStartError = false;
		onChange?.({ ...poll, window: newWindow });
	}
</script>

<form class="flex w-full flex-col gap-4" onsubmit={e => e.preventDefault()}>
	<div class="flex w-full flex-col gap-4 rounded-lg border p-4">
		<Heading tag="h2" class="text-sm font-semibold">Start</Heading>
		<div class="flex w-full flex-col gap-2 rounded-lg border p-4">
			<DateTimeInput class="grid grid-cols-2" required dateLabel="Start date" dateFormatOptions={DEFAULT_DATE_FORMAT_OPTIONS} timezone={poll.timezone || $user.timezone} datePlaceholder={DEFAULT_DATE_PLACEHOLDER} timeLabel="Start time" color={inputColor} timestamp={startDate} allDay={false} onChange={date => handleChange(date, "start")} />
		</div>
		<Helper>Fans can only cast votes while the voting window is open.</Helper>
	</div>
	<div class="flex w-full flex-col gap-4 rounded-lg border p-4">
		<Heading tag="h2" class="text-sm font-semibold">End</Heading>
		<div class="flex w-full flex-col gap-2 rounded-lg border p-4">
			<DateTimeInput class="grid grid-cols-2" required dateLabel="End date" dateFormatOptions={DEFAULT_DATE_FORMAT_OPTIONS} timezone={poll.timezone || $user.timezone} datePlaceholder={DEFAULT_DATE_PLACEHOLDER} timeLabel="End time" color={inputColor} timestamp={endDate} disabled={!poll.window?.end} allDay={false} onChange={date => handleChange(date, "end")} />
		</div>
		{#if showEndBeforeStartError}
			<Helper color="red" role="alert">End date must be later than start date</Helper>
		{/if}
		<Toggle class="w-fit" checked={!poll.window?.end} on:change={e => handleEndToggle(e)}>No end date</Toggle>
	</div>
</form>
